// InspectionAssigneesCell — the table's "Assigned to" cell: a pill that lazily
// fetches the row's auto-assigned users (POST /inspections/scope-users, same
// call the edit drawer makes) and lists them in a dropdown. No count upfront —
// the list contract carries none, so the pill just offers "View assignees".
// Rows without a project or level have no scope to resolve → dash.

import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Dropdown, Icon } from '@/components/common';
import {
  useLazyGetInspectionScopeUsersQuery,
  type InspectionListItem,
} from '@/api/endpoints/inspectionApi';
import initialsOf from '@/utils/initials';

const InspectionAssigneesCell = ({ inspection }: { inspection: InspectionListItem }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [fetchScopeUsers, scopeUsers] = useLazyGetInspectionScopeUsersQuery();

  if (!inspection.project || !inspection.companyLevel)
    return <span className='inspection-table__dash'>—</span>;

  const { project, companyLevel, companyNode } = inspection;

  // The cell lives inside a clickable row — keep the row's edit-drawer click out.
  const stop = (e: MouseEvent) => e.stopPropagation();

  const toggle = () => {
    if (!open)
      fetchScopeUsers(
        {
          project: project.id,
          companyLevel: companyLevel.id,
          companyNodes: companyNode ? [companyNode.id] : [],
        },
        true,
      );
    setOpen((o) => !o);
  };

  return (
    <span className='inspection-table__assignees' onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='start'
        width={240}
        portal
        trigger={
          <button
            type='button'
            className='inspection-table__assignees-trigger'
            aria-expanded={open}
            onClick={toggle}
          >
            <Icon name='users-round' size={15} />
            <span className='inspection-table__assignees-view'>
              {t('inspection_assigned_view')}
            </span>
          </button>
        }
      >
        <div className='inspection-table__assignees-panel'>
          {scopeUsers.isFetching ? (
            <span className='inspection-table__assignees-empty'>{t('text_loading')}</span>
          ) : (scopeUsers.data ?? []).length === 0 ? (
            <span className='inspection-table__assignees-empty'>
              {t('inspection_assigned_empty')}
            </span>
          ) : (
            (scopeUsers.data ?? []).map((person) => {
              const fullName = `${person.firstName} ${person.lastName}`.trim();
              return (
                <div key={person.id} className='inspection-table__assignees-row'>
                  <Avatar initials={initialsOf(fullName)} size={26} />
                  <span className='inspection-table__assignees-name'>{fullName}</span>
                </div>
              );
            })
          )}
        </div>
      </Dropdown>
    </span>
  );
};

export default InspectionAssigneesCell;
